import * as THREE from 'three';
import type { ImportedModelInstancedMeshUnit } from '../mesh-unit';
import type { ImportedModelInstancedTopologyDisplayEdges } from '../edge';
import { cloneOwnedMaterial } from '../../../material';
import { markCadObjectAsRuntimeDerived } from '../../../document';

/** 单 occurrence 的透明实例投影，借用正式几何并持续跟随正式矩阵槽。 */
export class ImportedModelTransparentInstanceMesh extends THREE.InstancedMesh {
  /** 随本投影释放的透明材质副本。 */
  private readonly ownedMaterials: THREE.Material[];
  /** 本投影的独占透明边线；共享本对象的 instanceMatrix。 */
  private edges: ImportedModelInstancedTopologyDisplayEdges | null = null;
  /** 防止重复释放借用关系。 */
  private disposed = false;

  /** @param unit 正式 Unit。 @param source 对应 source。 @param opacity 命令透明度。 */
  public constructor(
    private readonly unit: ImportedModelInstancedMeshUnit,
    private readonly source: THREE.Mesh,
    private opacity: number,
  ) {
    const materials = (Array.isArray(source.material) ? source.material : [source.material]).map(cloneOwnedMaterial);
    super(unit.object.geometry, Array.isArray(source.material) ? materials : materials[0], 1);
    this.ownedMaterials = materials;
    this.name = `imported-transparent-instance:${source.uuid}`;
    this.matrixAutoUpdate = false;
    this.frustumCulled = false;
    this.layers.mask = unit.object.layers.mask;
    this.renderOrder = unit.object.renderOrder;
    this.userData.skipSelection = true;
    this.userData.skipSelectionSubtree = true;
    this.userData.isImportedModelOverlay = true;
    markCadObjectAsRuntimeDerived(this);
    try {
      if (!unit.attachInstanceMatrixProjection(source, this)) throw new Error(`透明实例无法绑定矩阵：${source.uuid}`);
      this.setOpacity(opacity);
      unit.object.add(this);
      this.syncEdges();
    } catch (error) {
      this.dispose();
      throw error;
    }
  }

  /** @param opacity 多个 owner 合并后的临时透明度。 */
  public setOpacity(opacity: number): void {
    this.opacity = opacity;
    this.ownedMaterials.forEach((material, index) => {
      const originals = Array.isArray(this.source.material) ? this.source.material : [this.source.material];
      material.opacity = Math.min(originals[index]?.opacity ?? 1, opacity);
      material.transparent = true;
      material.depthWrite = false;
    });
    if (this.edges) {
      const materials = Array.isArray(this.edges.material) ? this.edges.material : [this.edges.material];
      materials.forEach(material => { material.opacity = opacity; material.transparent = true; material.depthWrite = false; });
    }
  }

  /** 显示模式改变时仅维护本实例的派生边线，不重新收集全装配拓扑。 */
  public syncEdges(): void {
    const enabled = this.unit.hasTopologyDisplayEdges();
    if (!enabled) { this.edges?.dispose(); this.edges = null; return; }
    if (!this.edges) {
      this.edges = this.unit.createTemporaryTopologyDisplayEdges(this.instanceMatrix);
      if (this.edges) this.add(this.edges);
    }
    this.edges?.setDisplayEdgeColor(this.unit.getTopologyDisplayEdgeColor());
    this.setOpacity(this.opacity);
  }

  /** 透明投影只绘制；拾取继续使用正式 Unit 的稳定 occurrence 映射。 */
  public override raycast(_raycaster: THREE.Raycaster, _hits: THREE.Intersection[]): void {}

  /** 幂等释放单槽缓冲、边线和独占材质，不释放正式几何。 */
  public override dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.unit.detachInstanceMatrixProjection(this.source, this);
    this.edges?.dispose();
    this.edges = null;
    this.removeFromParent();
    this.ownedMaterials.forEach(material => material.dispose());
    super.dispose();
  }
}
